import { useEffect, useRef, useCallback, useState } from 'react';

const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:8080/ws';
const RECONNECT_INTERVAL_BASE = 1000; // Start with 1 second
const MAX_RECONNECT_INTERVAL = 30000; // Max 30 seconds
const DEBOUNCE_DELAY = 500; // 500ms debounce for sending updates
const HEARTBEAT_INTERVAL = 30000; // Send ping every 30 seconds
const HEARTBEAT_TIMEOUT = 35000; // Expect pong within 35 seconds
const MAX_RECONNECT_ATTEMPTS = 50; // Max attempts before giving up (can reconnect later via visibility/online events)
const RECONNECT_JITTER = 0.3; // Add ±30% randomness to reconnect interval

/**
 * Custom hook for WebSocket connection with real-time sync
 * @param {string} token - JWT authentication token
 * @param {Function} onStateUpdate - Callback when state update is received from server
 * @returns {Object} - { sendUpdate, isConnected, error }
 */
export function useWebSocket(token, onStateUpdate) {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectIntervalRef = useRef(RECONNECT_INTERVAL_BASE);
  const reconnectAttemptsRef = useRef(0);
  const debounceTimeoutRef = useRef(null);
  const pendingUpdatesRef = useRef(new Map());
  const intentionalCloseRef = useRef(false);
  const currentTokenRef = useRef(token);
  const heartbeatIntervalRef = useRef(null);
  const heartbeatTimeoutRef = useRef(null);
  const lastPongRef = useRef(Date.now());
  const isReconnectingRef = useRef(false);

  // Update current token ref when token changes
  useEffect(() => {
    currentTokenRef.current = token;
  }, [token]);

  // Start heartbeat mechanism
  const startHeartbeat = useCallback(() => {
    // Clear any existing heartbeat timers
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
    }

    lastPongRef.current = Date.now();

    // Send ping every HEARTBEAT_INTERVAL
    heartbeatIntervalRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        const timeSinceLastPong = Date.now() - lastPongRef.current;
        
        // Check if we've received a pong recently
        if (timeSinceLastPong > HEARTBEAT_TIMEOUT) {
          console.warn('Heartbeat timeout - connection appears stale');
          // Force reconnection by closing the socket
          if (wsRef.current) {
            wsRef.current.close(4000, 'Heartbeat timeout');
          }
          return;
        }

        // Send ping
        try {
          wsRef.current.send(JSON.stringify({ type: 'ping', timestamp: new Date().toISOString() }));
          console.log('Sent heartbeat ping');
        } catch (err) {
          console.error('Failed to send ping:', err);
        }
      }
    }, HEARTBEAT_INTERVAL);
  }, []);

  // Stop heartbeat mechanism
  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = null;
    }
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!token) {
      console.log('No token available, skipping WebSocket connection');
      return;
    }

    // Don't connect if already connected or connecting
    if (wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    // Check if we've exceeded max reconnect attempts
    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      console.warn(`Max reconnect attempts (${MAX_RECONNECT_ATTEMPTS}) reached. Will retry on visibility change or network recovery.`);
      setError('Connection lost. Will retry when tab is active or network recovers.');
      return;
    }

    console.log(`Connecting to WebSocket... (attempt ${reconnectAttemptsRef.current + 1})`);
    isReconnectingRef.current = false;
    
    try {
      const ws = new WebSocket(`${WS_URL}?token=${token}`);

      ws.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        setError(null);
        // Reset reconnect tracking on successful connection
        reconnectIntervalRef.current = RECONNECT_INTERVAL_BASE;
        reconnectAttemptsRef.current = 0;
        isReconnectingRef.current = false;
        
        // Start heartbeat
        startHeartbeat();
        
        // Server will send full_state message with authoritative state
        console.log('Waiting for authoritative state from server');
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log('WebSocket message received:', message.type);

          // Handle pong response
          if (message.type === 'pong') {
            lastPongRef.current = Date.now();
            console.log('Received heartbeat pong');
            return;
          }

          if (message.type === 'full_state') {
            // Initial state from server
            console.log('Received full state from server');
            if (onStateUpdate) {
              onStateUpdate(message.data);
            }
          } else if (message.type === 'state_update') {
            // Update from another client
            console.log(`Received ${message.field} update from another client: data: ${JSON.stringify(message.data)}`);
            if (onStateUpdate) {
              onStateUpdate(message.data, message.field);
            }
          } else if (message.type === 'error') {
            console.error('WebSocket error message:', message.message);
            setError(message.message);
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };

      ws.onerror = (event) => {
        console.error('WebSocket error:', event);
        setError('WebSocket connection error');
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setIsConnected(false);
        wsRef.current = null;
        
        // Stop heartbeat
        stopHeartbeat();

        // Only attempt to reconnect if:
        // 1. Not an intentional close (logout/unmount)
        // 2. Still have a valid token
        // 3. Not a normal close (1000) or going away (1001)
        const shouldReconnect = !intentionalCloseRef.current &&
                               currentTokenRef.current &&
                               event.code !== 1000 &&
                               event.code !== 1001;

        if (shouldReconnect && !isReconnectingRef.current) {
          isReconnectingRef.current = true;
          reconnectAttemptsRef.current++;
          
          // Calculate interval with exponential backoff and jitter
          const baseInterval = Math.min(
            reconnectIntervalRef.current * 2,
            MAX_RECONNECT_INTERVAL
          );
          
          // Add random jitter to prevent thundering herd
          const jitter = baseInterval * RECONNECT_JITTER * (Math.random() * 2 - 1);
          const interval = Math.max(RECONNECT_INTERVAL_BASE, baseInterval + jitter);
          
          reconnectIntervalRef.current = baseInterval;
          
          console.log(`Reconnecting in ${Math.round(interval)}ms... (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, interval);
        } else if (!shouldReconnect) {
          console.log('Not reconnecting - intentional close or normal close code');
        }
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('Failed to create WebSocket:', err);
      setError('Failed to create WebSocket connection');
      setIsConnected(false);
      
      // Retry connection
      if (!intentionalCloseRef.current && currentTokenRef.current) {
        reconnectAttemptsRef.current++;
        const interval = reconnectIntervalRef.current;
        console.log(`Retrying in ${interval}ms...`);
        reconnectTimeoutRef.current = setTimeout(connect, interval);
      }
    }
  }, [token, onStateUpdate, startHeartbeat, stopHeartbeat]);

  // Send update to server with debouncing
  const sendUpdate = useCallback((field, data) => {
    // Store the update
    pendingUpdatesRef.current.set(field, data);

    // Clear existing debounce timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new debounce timeout
    debounceTimeoutRef.current = setTimeout(() => {
      // Send all pending updates
      pendingUpdatesRef.current.forEach((updateData, updateField) => {
        const message = {
          type: 'update',
          field: updateField,
          data: updateData,
          timestamp: new Date().toISOString()
        };
        
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          try {
            console.log(`Sending ${updateField} update to server`);
            wsRef.current.send(JSON.stringify(message));
          } catch (err) {
            console.error('Failed to send update (will be lost):', err);
            // Don't queue - server will be authoritative on reconnect
          }
        } else {
          console.warn('WebSocket not connected, update dropped (server is authoritative on reconnect)');
          // Don't queue - server will send authoritative state on reconnect
        }
      });

      // Clear pending updates
      pendingUpdatesRef.current.clear();
    }, DEBOUNCE_DELAY);
  }, []);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Page became visible');
        
        // Reset reconnect attempts when page becomes visible
        if (!isConnected && currentTokenRef.current) {
          console.log('Attempting to reconnect after page became visible');
          reconnectAttemptsRef.current = 0;
          reconnectIntervalRef.current = RECONNECT_INTERVAL_BASE;
          
          // Clear any pending reconnect timeout
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
          }
          
          connect();
        } else if (wsRef.current?.readyState === WebSocket.OPEN) {
          // Connection exists, verify it's healthy
          try {
            wsRef.current.send(JSON.stringify({ type: 'ping', timestamp: new Date().toISOString() }));
          } catch (err) {
            console.error('Connection health check failed:', err);
            // Connection appears broken, force reconnect
            if (wsRef.current) {
              wsRef.current.close(4001, 'Connection health check failed');
            }
          }
        }
      } else {
        console.log('Page became hidden');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [connect, isConnected]);

  // Handle network status changes
  useEffect(() => {
    const handleOnline = () => {
      console.log('Network came online');
      
      if (!isConnected && currentTokenRef.current && !intentionalCloseRef.current) {
        console.log('Attempting to reconnect after network recovery');
        reconnectAttemptsRef.current = 0;
        reconnectIntervalRef.current = RECONNECT_INTERVAL_BASE;
        
        // Clear any pending reconnect timeout
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        
        // Small delay to allow network to stabilize
        setTimeout(() => {
          connect();
        }, 1000);
      }
    };

    const handleOffline = () => {
      console.log('Network went offline');
      setError('Network connection lost');
      
      // Close existing connection if any
      if (wsRef.current) {
        intentionalCloseRef.current = true;
        wsRef.current.close(1000, 'Network offline');
        intentionalCloseRef.current = false;
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [connect, isConnected]);

  // Connect on mount and when token changes
  useEffect(() => {
    // Reset intentional close flag when token changes
    intentionalCloseRef.current = false;
    
    connect();

    // Cleanup on unmount or token change
    return () => {
      // Mark as intentional close to prevent reconnection
      intentionalCloseRef.current = true;
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      
      // Stop heartbeat
      stopHeartbeat();
      
      if (wsRef.current) {
        const currentState = wsRef.current.readyState;
        if (currentState === WebSocket.OPEN || currentState === WebSocket.CONNECTING) {
          console.log('Intentionally closing WebSocket connection');
          // Close with normal closure code
          wsRef.current.close(1000, 'Component unmount or logout');
        }
        // Always clear the ref to allow new connections
        wsRef.current = null;
      }
    };
  }, [connect, stopHeartbeat]);

  return {
    sendUpdate,
    isConnected,
    error
  };
}
